import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { getHistorial } from '../../api/client'
import { formatFechaHora, formatFecha } from './tipos'

const PAGINA = 50
const CAMPOS_UNIDAD = { codigo: 'codigo', serie: 'serie', proyecto: 'proyecto', estado_actual: 'estado', fecha_ingreso: 'fechaIngreso' }

// Long values (option lists, colors) shortened so one entry stays one line.
function corto(v, max = 60) {
  if (v == null) return ''
  let s = String(v)
  try {
    const j = JSON.parse(s)
    if (Array.isArray(j)) s = j.join(', ')
    else if (j && typeof j === 'object') s = Object.entries(j).map(([k, c]) => `${k}: ${c}`).join(', ')
  } catch { /* plain text */ }
  return s.length > max ? `${s.slice(0, max)}…` : s
}

function Val({ v }) {
  return <span className="font-medium text-gray-800">{corto(v)}</span>
}

function Campo({ c }) {
  return <span className="font-medium text-gray-800">{c}</span>
}

const flecha = <span className="text-gray-400"> → </span>

// One log row (models.CambioLog) as a sentence.
function Descripcion({ e }) {
  const { t } = useTranslation('engineering')
  const k = 'unidades.historial.'

  if (e.entidad === 'unidad') {
    if (e.accion === 'crear') return <>{t(`${k}createdUnit`)}</>
    if (e.accion === 'eliminar') return <>{t(`${k}deletedUnit`)}</>
    const campo = t(`unidades.${CAMPOS_UNIDAD[e.campo] || 'codigo'}`)
    return <>{t(`${k}changed`)} <Campo c={campo} />: <Val v={e.antes ?? '—'} />{flecha}<Val v={e.despues ?? '—'} /></>
  }
  if (e.entidad === 'parte') {
    const num = `#${e.parte_numero}`
    if (e.accion === 'crear') return <>{t(`${k}openedParte`)} <Campo c={num} /></>
    if (e.accion === 'cerrar') return <>{t(`${k}closedParte`)} <Campo c={num} /> <span className="text-gray-400">({formatFecha(e.despues)})</span></>
    if (e.accion === 'eliminar') return <>{t(`${k}deletedParte`)} <Campo c={num} /></>
    const campo = t(`partes.campos.${e.campo}`, e.campo)
    const fmt = (v) => (e.campo?.startsWith('fecha') ? formatFecha(v) : v)
    return <>{t(`${k}changed`)} <Campo c={campo} /> {t(`${k}ofParte`)} {num}: <Val v={fmt(e.antes) ?? '—'} />{flecha}<Val v={fmt(e.despues) ?? '—'} /></>
  }
  if (e.entidad === 'valor') {
    if (e.antes == null) return <>{t(`${k}set`)} <Campo c={e.campo} />: <Val v={e.despues} /></>
    if (e.despues == null) return <>{t(`${k}cleared`)} <Campo c={e.campo} /> <span className="text-gray-400">({t(`${k}was`)} {corto(e.antes)})</span></>
    return <>{t(`${k}changed`)} <Campo c={e.campo} />: <Val v={e.antes} />{flecha}<Val v={e.despues} /></>
  }
  if (e.entidad === 'archivo') {
    return e.accion === 'crear'
      ? <>{t(`${k}attached`)} <Val v={e.despues} /> {t(`${k}in`)} <Campo c={e.campo} /></>
      : <>{t(`${k}removedFile`)} <Val v={e.antes} /> {t(`${k}from`)} <Campo c={e.campo} /></>
  }
  if (e.entidad === 'columna') {
    if (e.accion === 'crear') return <>{t(`${k}createdColumn`)} <Campo c={e.campo} /> <span className="text-gray-400">({t(`unidades.planilla.tipos.${e.despues}`, e.despues)})</span></>
    if (e.accion === 'eliminar') return <>{t(`${k}deletedColumn`)} <Campo c={e.campo} /></>
    return <>{t(`${k}changedColumn`)} <Campo c={e.campo} />: <Val v={e.antes ?? '—'} />{flecha}<Val v={e.despues ?? '—'} /></>
  }
  return <>{e.accion} {e.campo}</>
}

// The change log, newest first. With `unidadId` or `parteId`: one record's
// history (inside its panel; a unit's includes what happened to its partes).
// Without: every change of `ambito` (unidades | partes), filterable by user,
// with each row's unit code / parte number clickable (onOpenUnidad /
// onOpenParte).
export default function HistorialLista({ unidadId, parteId, ambito, proyecto, onOpenUnidad, onOpenParte, recarga = 0 }) {
  const { t } = useTranslation('engineering')
  const [filas, setFilas] = useState([])
  const [hayMas, setHayMas] = useState(false)
  const [error, setError] = useState('')
  // Which request the rows on screen answer; "loading" = not the current one.
  const [cargado, setCargado] = useState(null)
  const [usuario, setUsuario] = useState('')
  const [usuarioQ, setUsuarioQ] = useState('')
  const requestId = useRef(0)

  // Typing in the user box filters after a short pause, not per keystroke.
  useEffect(() => {
    const id = setTimeout(() => setUsuarioQ(usuario.trim()), 300)
    return () => clearTimeout(id)
  }, [usuario])

  const params = useCallback((extra = {}) => ({
    limit: PAGINA,
    ...(unidadId != null ? { unidad_id: unidadId } : {}),
    ...(parteId != null ? { parte_id: parteId } : {}),
    ...(ambito ? { ambito } : {}),
    ...(proyecto ? { proyecto } : {}),
    ...(usuarioQ ? { usuario: usuarioQ } : {}),
    ...extra,
  }), [unidadId, parteId, ambito, proyecto, usuarioQ])

  const clave = `${JSON.stringify(params())}|${recarga}`
  const loading = cargado !== clave

  useEffect(() => {
    const id = ++requestId.current
    getHistorial(params())
      .then(rows => { if (id === requestId.current) { setFilas(rows); setHayMas(rows.length === PAGINA); setError('') } })
      .catch(() => { if (id === requestId.current) setError(t('unidades.historial.loadFailed')) })
      .finally(() => { if (id === requestId.current) setCargado(clave) })
  }, [params, recarga, t, clave])

  const masAntiguos = async () => {
    const id = requestId.current
    try {
      const rows = await getHistorial(params({ antes_de: filas[filas.length - 1].id }))
      if (id !== requestId.current) return
      setFilas(fs => [...fs, ...rows])
      setHayMas(rows.length === PAGINA)
    } catch { setError(t('unidades.historial.loadFailed')) }
  }

  const global = unidadId == null && parteId == null

  return (
    <div className="space-y-3">
      {global && (
        <input type="search" value={usuario} onChange={e => setUsuario(e.target.value)}
          placeholder={t('unidades.historial.filterUser')}
          className="w-64 border border-gray-200 rounded-lg px-3 py-2 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200" />
      )}
      {error && <p className="text-red-500 text-sm">{error}</p>}
      {loading && filas.length === 0 ? (
        <p className="text-gray-400 text-sm">{t('unidades.loading')}</p>
      ) : filas.length === 0 ? (
        <p className="text-gray-400 text-sm py-6 text-center">{t('unidades.historial.empty')}</p>
      ) : (
        <ol className={`divide-y divide-gray-100 ${global ? 'bg-white border border-gray-200 rounded-xl' : ''}`}>
          {filas.map(e => (
            <li key={e.id} className={`flex gap-3 text-sm ${global ? 'px-4 py-2.5' : 'py-2.5'}`}>
              <div className="w-36 shrink-0 text-xs text-gray-400 pt-0.5 tabular-nums">{formatFechaHora(e.fecha)}</div>
              {global && (
                <div className="w-24 shrink-0">
                  {e.parte_id && onOpenParte ? (
                    <button type="button" onClick={() => onOpenParte(e.parte_id)}
                      className="font-medium text-gray-900 hover:underline">#{e.parte_numero}</button>
                  ) : e.unidad_codigo && (e.unidad_id && onOpenUnidad
                    ? <button type="button" onClick={() => onOpenUnidad(e.unidad_id)}
                        className="font-medium text-gray-900 hover:underline">{e.unidad_codigo}</button>
                    : <span className="font-medium text-gray-500">{e.unidad_codigo}</span>)}
                  {!e.unidad_codigo && <span className="text-xs text-gray-400">{e.proyecto}</span>}
                </div>
              )}
              <div className="min-w-0 flex-1 text-gray-600 break-words">
                <span className={`font-medium ${e.usuario_id ? 'text-gray-900' : 'text-gray-400 italic'}`}>{e.usuario_nombre}</span>{' '}
                <Descripcion e={e} />
                {/* A parte's cell or file seen from its unit's history: say which parte. */}
                {e.parte_numero != null && e.entidad !== 'parte' && parteId == null && (
                  <span className="ml-1.5 text-xs text-gray-400">· {t('unidades.historial.inParte', { n: e.parte_numero })}</span>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
      {hayMas && (
        <button type="button" onClick={masAntiguos} className="text-xs font-medium text-gray-500 hover:text-gray-900">
          {t('unidades.historial.more')}
        </button>
      )}
    </div>
  )
}
